import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// Atlaskit Drag-and-Drop Imports
import {
    draggable,
    dropTargetForElements,
    monitorForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import {
    attachClosestEdge,
    Edge,
    extractClosestEdge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";

// Local Components
import Card from './Card';
import AddCardButton from './AddCardButton';

// Hooks
import useOutsideClick from '../../hooks/useOutsideClick';


// Redux Actions
import { showSuccess, showError } from '../../utils/snackbarHelpers';
import { fetchWorkspaces } from '../../redux/Slices/workspaceSlice';
import { fetchCardsByListId, updateTotalCardsOnMove, incrementTotalCards, decrementTotalCards } from '../../redux/Slices/actionlistSlice';
import { handleArchiveList, pushArchivedList } from '../../redux/Slices/cardSlice';

// Config
import { config } from '../../config';

// Virtualization
import { useVirtualizer } from '@tanstack/react-virtual';
import Spinner from '../Global/Spinner';
import useHandlePopoverClick from '../../hooks/useHandlePopoverClick';

import './scss/ActionListPND.scss';
import Add from '../icons/Add';
import MoreHoriz from '../icons/MoreHoriz';
import BouncingDots from '../Global/BouncingDots';
import { darken, rgbaColor } from '../../utils/helpers';
import { useTheme } from '../../context/ThemeContext';
import { getCurrentThemeColor, normalizeTheme } from '../../utils/listColorMapping';
import Close from '../icons/Close';



// Compute accessible text color (black/white) based on background
const getContrastTextColor = (bg) => {
    if (!bg) return 'var(--single-card-text-color)';
    // Normalize hex like #RRGGBB or rgb(a)
    const hex = bg.startsWith('#') ? bg.replace('#', '') : null;
    let r, g, b;
    if (hex && (hex.length === 6 || hex.length === 3)) {
        const h = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex;
        r = parseInt(h.substring(0, 2), 16);
        g = parseInt(h.substring(2, 4), 16);
        b = parseInt(h.substring(4, 6), 16);
    } else if (bg.startsWith('rgb')) {
        const nums = bg.replace(/rgba?\(|\)/g, '').split(',').map(n => parseFloat(n.trim()));
        [r, g, b] = nums;
    } else {
        // Fallback to white text for unknown values
        return '#ffffff';
    }
    // Relative luminance
    const [R, G, B] = [r, g, b].map(c => {
        c /= 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    const luminance = 0.2126 * R + 0.7152 * G + 0.0722 * B;
    return luminance > 0.5 ? '#172b4d' : '#ffffff';
};


// Stable default to avoid creating a new object reference in selector fallbacks
const EMPTY_ACTION_LIST_STATE = {
    cards: [],
    status: 'idle',
    error: null,
    hasMore: true,
    totalCards: 0,
};

const ActionListPND = ({ actionList, isOpen, onToggleOptions, onDelete, board, setBoard, boardId, hasLoadedInitialCards, boardScrollRef, isReadOnly = false }) => {

    const { _id, title, cards: initialCards } = actionList;

    const actionListState = useSelector(state => state.actionList.actionLists[_id] || EMPTY_ACTION_LIST_STATE);
    const { cards: reduxCards, hasMore, totalCards } = actionListState;


    const [isLoadingMoreCards, setIsLoadingMoreCards] = useState(false);
    const [loadedCardBatches, setLoadedCardBatches] = useState(new Set());

    const dispatch = useDispatch();
    const { theme } = useTheme();

    // Track the actual resolved theme (handles "system" theme properly)
    const [actualTheme, setActualTheme] = useState(() => normalizeTheme(theme));

    // Listen for data-theme attribute changes (which ThemeContext manages)
    useEffect(() => {
        const updateActualTheme = () => {
            const newActualTheme = normalizeTheme(theme);
            setActualTheme(newActualTheme);
        };

        // Update immediately
        updateActualTheme();

        // Listen for data-theme attribute changes on document element
        // This will catch both manual theme changes and system theme changes
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
                    updateActualTheme();
                }
            });
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-theme']
        });

        return () => observer.disconnect();
    }, [theme]);

    const [showForm, setShowForm] = useState(false);
    const [showTopForm, setShowTopForm] = useState(false);
    const [cardTitle, setCardTitle] = useState('');
    const [cardDescription, setCardDescription] = useState('');
    const [currentCards, setCurrentCards] = useState(initialCards || []);
    // const [isDeleting, setIsDeleting] = useState(false); // Uncomment if needed for delete functionality
    const [isArchiving, setIsArchiving] = useState(false);
    const [listTitle, setListTitle] = useState(title);
    const [isListChanged, setListChanged] = useState(false);

    const filterQuery = useSelector((state) => state.filterQuery);

    // Reset loadedCardBatches when filter changes to allow loading more filtered cards
    useEffect(() => {
        setLoadedCardBatches(new Set());
    }, [filterQuery]);

    const cardTitleRef = useRef(null);
    const cardTopTitleRef = useRef(null);
    const actionListContainerRef = useRef(null);
    const actionListTitleRef = useRef(null);

    const cardListRef = useRef(null);
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const [isCardDragging, setCardDragging] = useState(false);
    const [closestEdge, setClosestEdge] = useState(null);

    // We'll use our own isScrolling state instead of the hook

    const actionListRef = useRef(null);
    const listHeaderRef = useRef(null);
    const addNewCardFormRef = useRef(null);
    const hiddenListRef = useRef(null);
    const [containerHeight, setContainerHeight] = useState(720);
    const [isEditing, setIsEditing] = useState(false);

    // Track scrolling state
    const [isScrolling, setIsScrolling] = useState(false);


    const [visibleListOptions, setVisibleListOptions] = useState({});

    const { handlePopoverClick } = useHandlePopoverClick();

    const toggleListOptions = (id) => {
        setVisibleListOptions((prevState) => ({
            ...prevState,
            [id]: !prevState[id],
        }));
    };


    useOutsideClick(actionListRef, () => {
        setVisibleListOptions({});
    });


    const moveActionList = (sourceId, targetId) => {
        setBoard((prevBoard) => {
            const updatedActionLists = [...prevBoard.actionLists];

            const sourceIndex = updatedActionLists.findIndex(list => list._id === sourceId);
            const targetIndex = updatedActionLists.findIndex(list => list._id === targetId);

            if (sourceIndex === -1 || targetIndex === -1) return prevBoard;

            // Move the source action list to the target position
            const [movedList] = updatedActionLists.splice(sourceIndex, 1);
            updatedActionLists.splice(targetIndex, 0, movedList);

            // Reassign the order based on the new array position
            const reorderedActionLists = updatedActionLists.map((list, index) => ({
                ...list, // Clone to avoid mutation
                order: index + 1, // Assign new order value
            }));

            return {
                ...prevBoard,
                actionLists: reorderedActionLists,
            };
        });
    };


    // Throttled moveCard function to prevent ResizeObserver errors during continuous drag
    const moveCardThrottled = useRef(null);

    const moveCard = useCallback((cardId, targetListId, targetPosition) => {
        // Clear any existing timeout
        if (moveCardThrottled.current) {
            clearTimeout(moveCardThrottled.current);
        }

        // Throttle the move operation to prevent ResizeObserver loop errors
        moveCardThrottled.current = setTimeout(() => {
            setBoard((prevBoard) => {
                let cardToMove = null;
                let sourceListId = null;

                // Find the source list and card to move
                const updatedActionLists = prevBoard.actionLists.map((list) => {
                    const newList = { ...list }; // Clone the list to avoid mutation
                    if (newList.cards?.some((card) => card._id === cardId)) {
                        sourceListId = newList._id;
                        newList.cards = newList.cards.reduce((acc, card) => {
                            if (card._id === cardId) {
                                cardToMove = {
                                    ...card,
                                    actionList: { _id: targetListId },
                                    // Mark card as being dragged optimistically when moved to different list
                                    isOptimisticallyMoved: sourceListId !== targetListId
                                };
                                return acc; // Exclude this card from the new array
                            }
                            return [...acc, card]; // Include other cards
                        }, []);
                    }
                    return newList;
                });

                // Ensure card is found
                if (!cardToMove) {
                    console.error("Card not found");
                    return prevBoard;
                }

                // Update the target list with the card at the target position
                const finalActionLists = updatedActionLists.map((list) => {
                    const newList = { ...list }; // Clone the list
                    if (newList._id === targetListId) {
                        const newTargetCards = [...newList.cards];
                        newTargetCards.splice(targetPosition, 0, cardToMove); // Insert card at target position

                        // Reorder all cards in the target list and update their order and actionList._id
                        newList.cards = newTargetCards.map((card, index) => ({
                            ...card,
                            order: index, // Update order
                            actionList: { _id: targetListId }, // Ensure all cards in target list have the correct list ID
                        }));
                    }
                    return newList; // Return the modified or unmodified list
                });

                // Reorder cards in the source list if it has been modified
                if (sourceListId) {
                    finalActionLists.forEach((list) => {
                        if (list._id === sourceListId) {
                            list.cards = list.cards.map((card, index) => ({
                                ...card,
                                order: index, // Update order for the source list
                            }));
                        }
                    });
                }

                return { ...prevBoard, actionLists: finalActionLists };
            });
        }, 16); // 16ms throttle (~60fps) to prevent ResizeObserver loop errors
    }, [setBoard]);

    // Cleanup throttled function on unmount
    useEffect(() => {
        return () => {
            if (moveCardThrottled.current) {
                clearTimeout(moveCardThrottled.current);
            }
        };
    }, []);


    const updateBoardOrder = async () => {
        const actionListOrders = board.actionLists.map(list => ({ _id: list._id, order: list?.order }));

        try {

            await fetch(`${config.API_URI}/v1/boards/${boardId}/order`, {
                method: 'PUT',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',

                },
                credentials: 'include',
                body: JSON.stringify({ actionListOrders }), // Assuming card orders are not being updated
            });
        } catch (error) {
            console.error('Error updating action list order:', error);
        }
    };

    const updateCardsOrder = async (sourceListId, cardId, targetListId, targetPosition) => {


        try {

            await fetch(`${config.API_URI}/v1/boards/${boardId}/cards/order`, {
                method: 'PUT',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',

                },
                credentials: 'include',
                body: JSON.stringify({ sourceListId, cardId, targetListId, targetPosition }),
            });
        } catch (error) {
            console.error('Error moving card:', error);
        }
    };


    const [isDragging, setDragging] = useState(false);

    useEffect(() => {
        const element = actionListRef.current;
        const containerElement = actionListContainerRef.current;

        if (!element) return; // Early exit if ref.current is null or undefined

        // Skip drag-and-drop setup if read-only
        if (isReadOnly) return;

        const autoScroll = autoScrollForElements({
            element: containerElement,
            getConfiguration: () => ({
                maxScrollSpeed: 'fast',
                allowedAxis: 'vertical', // Only allow vertical scrolling within lists
            }),
        });

        const combined = combine(
            // Draggable configuration for the action list
            draggable({
                element,
                dragHandle: element,
                getInitialData: () => ({ ...actionList, type: 'ACTIONLIST' }),

                onDrag() {
                    setDragging(true);
                    // Add class to all other items
                    document.querySelectorAll('.action-list-inner').forEach(item => {
                        if (!item.classList.contains('dragging-start') || !item.classList.contains('other-dragging')) {
                            item.classList.add('other-dragging');
                        }
                    });
                },
                onDrop() {
                    setDragging(false);
                    // Remove class from all other items
                    document.querySelectorAll('.action-list-inner').forEach(item => {
                        item.classList.remove('other-dragging');
                    });
                }
            }),

            // Drop target configuration for the card list
            dropTargetForElements({
                element: cardListRef.current,
                getData: () => ({ ...actionList, type: 'ACTIONLIST' }),
                canDrop: (args) => args.source.data.type === "CARD",
                getIsSticky: () => true,
                // Improve drop sensitivity - allow drops as soon as cursor enters the area
                hitboxPadding: 5, // Add 5px padding around the drop zone for easier targeting

                // Events for card drag and drop interactions
                onDragEnter: () => setIsDraggingOver(true),
                onDragLeave: () => setIsDraggingOver(false),
                onDragStart: () => setIsDraggingOver(true),
                onDrop: () => setIsDraggingOver(false),
            }),

            // Drop target configuration for the action list
            dropTargetForElements({
                element,
                getIsSticky: () => true,
                // Improve drop sensitivity for list-to-list drops
                hitboxPadding: 8, // Add padding for easier targeting
                getData: ({ input, element }) => {
                    const data = { ...actionList, type: 'ACTIONLIST' };
                    return attachClosestEdge(data, {
                        input,
                        element,
                        allowedEdges: ["left", "right"],
                        // Improve edge detection sensitivity
                        threshold: 0.3, // Trigger edge detection when cursor is within 30% of the edge
                    });
                },

                // Events when dragging enters the action list
                onDragEnter: (args) => {
                    setClosestEdge(extractClosestEdge(args.self.data));
                    const { source, self } = args;
                    const target = self;

                    // If dragging a card, set dragging state and move the card within the list
                    if (source.data.type === 'CARD') {
                        setIsDraggingOver(true);
                        // Move card to the last position (end of the list)
                        const lastPosition = currentCards.length;
                        moveCard(source.data._id, target.data._id, 0);
                    }
                    // If dragging a list, set appropriate dragging state
                    else if (source.data.type === 'ACTIONLIST') {
                        // setIsDraggingOver(true);
                        moveActionList(source.data._id, target.data._id); // Assuming you have a moveActionList function
                    }
                },

                // Continuously update closest edge while dragging
                onDrag: (args) => {
                    const closestEdge = extractClosestEdge(args.self.data);
                    setClosestEdge(closestEdge);
                },

                // Events when dragging leaves the action list
                onDragLeave: () => {
                    setClosestEdge(null);
                    setIsDraggingOver(false);
                },

                // Events when an item is dropped onto the action list
                onDrop: ({ source, self }) => {
                    setClosestEdge(null);
                    setIsDraggingOver(false);
                    setDragging(false);

                    if (source.data.type === 'CARD') {
                        // Handle card movement logic
                        const movingCardId = source.data._id;
                        const sourceListId = source.data.actionList?._id || source.data.actionList;
                        const targetListId = _id; // current list id

                        // Clear optimistic move flag for all cards when drop occurs
                        setBoard((prevBoard) => ({
                            ...prevBoard,
                            actionLists: prevBoard.actionLists.map((list) => ({
                                ...list,
                                cards: list.cards.map((card) => ({
                                    ...card,
                                    isOptimisticallyMoved: false
                                }))
                            }))
                        }));

                        // If the source list is different from the target list, update totalCards optimistically
                        if (sourceListId !== targetListId) {
                            dispatch(updateTotalCardsOnMove({ sourceListId, targetListId }));
                        }

                        // Compute target position from currentCards to avoid relying on stale self.data
                        const idx = currentCards.findIndex(c => c._id === movingCardId);

                        const card = self.data.cards.find(c => c._id === source.data._id);


                        // const targetPosition = idx >= 0 ? idx : currentCards.length;
                        const targetPosition = card?.order;

                        // If this is an optimistic placeholder id, skip server call to avoid 4xx/5xx
                        if (typeof movingCardId === 'string' && movingCardId.startsWith('optimistic-')) {
                            return;
                        }

                        updateCardsOrder(sourceListId, movingCardId, targetListId, targetPosition);
                    }
                    else if (source.data.type === 'ACTIONLIST') {
                        // Handle action list movement logic
                        moveActionList(source.data._id, self.data._id);
                        updateBoardOrder();
                    }
                },
            })

        );


        return () => {

            combined();
            autoScroll();

        };

    }, [actionList, actionListRef.current, isReadOnly]); // Re-run effect only if actionList or ref.current changes


    const [listHighlight, setListHighlight] = useState(false);

    useEffect(() => {
        const element = actionListRef.current;

        if (!element) return;

        const monitorConfig = {
            element,
            onDrag(args) {

                const { source, location } = args;
                const target = location.current.dropTargets[0];

                if (!target || target.data.type !== 'ACTIONLIST') { // Check if target is a card
                    return;
                }

                if (target.data._id === actionList._id && source.data.type === 'ACTIONLIST' && target.data.type === 'ACTIONLIST') {
                    setListHighlight(true);
                } else {
                    setListHighlight(false);
                }
            },
            onDrop() {
                setListHighlight(false);
            },
        };

        return monitorForElements(monitorConfig);
    }, [actionList]);


    useEffect(() => {
        // Find the list by _id with strict matching to prevent cross-contamination
        const updatedList = board.actionLists.find((list) => list._id === _id);

        if (updatedList && updatedList.cards) {
            // Additional safety check: verify this is actually the correct list
            // by checking if the list ID matches exactly
            if (updatedList._id !== _id) {
                console.warn('[ActionListPND] List ID mismatch detected, skipping card update');
                return;
            }

            // Ensure we're not setting duplicate cards
            const uniqueCards = Array.from(
                new Map(updatedList.cards.map(card => [card._id, card])).values()
            );

            // Only update if cards have actually changed to prevent unnecessary re-renders
            setCurrentCards(prevCards => {
                // Quick check: if lengths differ, definitely update
                if (prevCards.length !== uniqueCards.length) {
                    return uniqueCards;
                }

                // Deep check: compare card IDs
                const prevCardIds = prevCards.map(c => c._id).join(',');
                const newCardIds = uniqueCards.map(c => c._id).join(',');

                if (prevCardIds !== newCardIds) {
                    return uniqueCards;
                }

                // Cards are the same, but check if any card content changed
                const hasContentChange = uniqueCards.some((newCard, idx) => {
                    const prevCard = prevCards[idx];
                    return JSON.stringify(newCard) !== JSON.stringify(prevCard);
                });

                return hasContentChange ? uniqueCards : prevCards;
            });
        }
    }, [board, _id]);

    // Add new card to the list
    const handleAddCard = async (e, position) => {
        e.preventDefault();
        dispatch(incrementTotalCards({ actionListId: _id }));

        const optimisticId = `optimistic-${Date.now()}`; // Unique temporary ID
        const optimisticCard = {
            _id: optimisticId,
            actionListId: _id,
            actionList: { _id },
            title: cardTitle,
            description: cardDescription,
            users: [],
            order: position === 'top' ? 0 : currentCards.length,
            isOptimistic: true,
            priority: 'Normal',
        };

        // Optimistically update the board state
        setBoard((prevBoard) => ({
            ...prevBoard,
            cardsCount: prevBoard.cardsCount + 1,
            actionLists: prevBoard.actionLists.map((list) =>
                list._id === _id
                    ? {
                        ...list,
                        cards: position === 'top'
                            ? [optimisticCard, ...list.cards]
                            : [...list.cards, optimisticCard],
                    }
                    : list
            ),
        }));

        // Clear input fields and hide the form
        setCardTitle('');
        setCardDescription('');
        handleShowForm(position);

        try {

            const response = await fetch(config.API_URI + '/v1/cards', {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',

                },
                credentials: 'include',
                body: JSON.stringify({
                    actionListId: _id,
                    title: cardTitle,
                    description: cardDescription,
                    position,
                    users: [],
                }),
            });

            if (!response.ok) throw new Error('Failed to add card');

            const { card: newCard } = await response.json();

            // Replace the optimistic card with the real one wherever it currently is
            // Track current location inside setBoard to avoid stale closure issues
            let currentListId = null;
            let currentIndex = -1;

            setBoard((prevBoard) => {
                // Find current location using FRESH state (not stale closure)
                const currentList = prevBoard.actionLists.find((l) => 
                    (l.cards || []).some((c) => c._id === optimisticId)
                );
                currentListId = currentList?._id;
                currentIndex = currentList ? currentList.cards.findIndex((c) => c._id === optimisticId) : -1;

                return {
                    ...prevBoard,
                    actionLists: prevBoard.actionLists.map((list) => {
                        const idx = (list.cards || []).findIndex((c) => c._id === optimisticId);
                        if (idx !== -1) {
                            const updatedCards = [...list.cards];
                            const prevCard = updatedCards[idx];
                            const merged = {
                                ...newCard,
                                order: prevCard?.order ?? idx,
                                actionList: { _id: list._id },
                                isOptimisticallyMoved: false,
                            };
                            updatedCards[idx] = merged;
                            return { ...list, cards: updatedCards };
                        }
                        return list;
                    }),
                };
            });

            // If the card was moved to a different list before the create returned, sync that move now
            if (currentListId && currentListId !== _id) {
                const pos = currentIndex >= 0 ? currentIndex : 0;
                updateCardsOrder(_id, newCard._id, currentListId, pos);
            }

            // Scroll to the correct position
            position === 'top' ? scrollToTop() : scrollToBottom();
        } catch (error) {
            // Remove the optimistic card from `setBoard` if request fails
            setBoard((prevBoard) => ({
                ...prevBoard,
                cardsCount: prevBoard.cardsCount - 1,
                actionLists: prevBoard.actionLists.map((list) =>
                    list._id === _id
                        ? {
                            ...list,
                            cards: list.cards.filter((card) => card._id !== optimisticId),
                        }
                        : list
                ),
            }));

            // Show error toast
            dispatch(decrementTotalCards({ actionListId: _id }));
            showError(dispatch, `Error adding card: ${error.message}`);
        }
    };




    // Scroll to bottom after adding a new card
    const scrollToBottom = () => {
        setTimeout(() => {
            if (actionListContainerRef.current) {
                actionListContainerRef.current.scrollTop = actionListContainerRef.current.scrollHeight;
            }
        }, 0);
    };
    const scrollToTop = () => {
        setTimeout(() => {
            if (actionListContainerRef.current) {
                actionListContainerRef.current.scrollTop = 0;
            }
        }, 0);
    };

    // recalculateContainerHeight function is defined above

    // Show the add card form
    const handleShowForm = (position) => {
        if (position === 'top') {
            setShowTopForm(true);
            setShowForm(false);
        } else {
            setShowForm(true);
            setShowTopForm(false);
        }

        // Use a small delay to ensure state updates before focusing
        setTimeout(() => {
            // Focus the appropriate textarea
            if (position === 'top' && cardTopTitleRef.current) {
                cardTopTitleRef.current.focus();
                scrollToTop();
            } else if (cardTitleRef.current) {
                cardTitleRef.current.focus();
                scrollToBottom();
            }
        }, 10);
    };

    // Hide the add card form
    const handleHideForm = () => {
        setShowForm(false);
        setShowTopForm(false);
        setCardTitle('');
    };



    // Archive the action list
    const handleArchivedList = async () => {
        if (isArchiving) return;
        setIsArchiving(true);

        try {
            // Optimistic update: Add to archive list and remove from board
            dispatch(pushArchivedList({ ...actionList, archived: true }));
            onDelete(_id); // Removes from board state via parent component

            await dispatch(handleArchiveList({ boardId, listId: _id, archivedStatus: true })).unwrap();

            showSuccess(dispatch, 'List archived successfully!');
        } catch (error) {
            // Rollback is usually complex for lists, but here we can just show error
            // and assume the user will refresh or the board state will sync
            showError(dispatch, 'Error archiving the list: ' + error.message);
        } finally {
            setIsArchiving(false);
        }
    };

    // Update the list title
    const handleUpdateListTitle = async (newTitle) => {
        try {

            const response = await fetch(`${config.API_URI}/v1/actionLists/${_id}`, {
                method: 'PUT',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',

                },
                credentials: 'include',
                body: JSON.stringify({ boardId, title: newTitle }),
            });

            if (!response.ok) throw new Error('Failed to update the list title');

            // dispatch(fetchWorkspaces());
            // showSuccess(dispatch, 'List title updated successfully!');
        } catch (error) {
            showError(dispatch, 'Error updating the list title: ' + error.message);
        }
    };

    // Handle clicking outside the list title input to save changes
    useOutsideClick(actionListTitleRef, () => {
        if (isListChanged) {
            handleUpdateListTitle(listTitle);
            setListChanged(false);
        }
    });

    // Adjust textarea height based on content for card title
    useEffect(() => {
        if (cardTitleRef.current) {
            adjustTextareaHeight(cardTitleRef.current, cardTitle, 16);
        }
        if (cardTopTitleRef.current) {
            adjustTextareaHeight(cardTopTitleRef.current, cardTitle, 16);
        }
    }, [cardTitle]);

    // Adjust textarea height based on content for list title
    useEffect(() => {
        if (actionListTitleRef.current) {
            adjustTextareaHeight(actionListTitleRef.current, listTitle, 6);
        }
    }, [listTitle, isEditing]);

    // Adjust the height of the textarea based on its content
    const adjustTextareaHeight = (textarea, _content, minus) => {
        textarea.style.height = '24px';
        const scrollHeight = textarea.scrollHeight - minus;
        textarea.style.height = `${scrollHeight}px`;
    };

    // Removed unused style objects


    // List background color and text color for Pro feature with theme-aware mapping
    const storedListColor = actionList?.color || null;
    const storedListTextColor = actionList?.textColor || null;

    // Get the current theme-appropriate colors using the actual resolved theme
    const currentThemeColors = useMemo(() => {
        if (storedListColor) {
            return getCurrentThemeColor(storedListColor, actualTheme);
        }
        return { bg: null, text: null };
    }, [storedListColor, actualTheme]);

    const listBgColor = currentThemeColors.bg;
    const listTextColor = currentThemeColors.text;

    const headerTextColor = useMemo(() => {
        // If list has a background color set, use the theme-appropriate text color
        if (listBgColor) {
            return listTextColor;
        }
        // If no list color is set, use theme-based text color
        return 'var(--single-card-text-color)';
    }, [listBgColor, listTextColor]);

    // Define styles based on dragging state
    const style = {
        ...(listHighlight ? { opacity: '0', backgroundColor: 'black', border: 'none', userSelect: 'none' } : {}),
        ...(isDraggingOver ? { outline: '2px solid var(--brand-color)' } : {}),
        ...(listBgColor ? { ['--actionlist-background-color']: darken(listBgColor, 20) } : {}),
        ...(listBgColor ? { ['--add-card-button-hover-background-color']: rgbaColor(listBgColor, .5) } : {}),
        ...(headerTextColor ? { ['--actionlist-contrast-text-color']: headerTextColor } : {}),
        marginTop: '2px',
    };

    // Memoize the virtualizer configuration to prevent unnecessary re-creation
    const virtualizer = useVirtualizer({
        horizontal: false,
        count: currentCards?.length,
        getScrollElement: () => actionListContainerRef.current,
        // Key virtual items by stable card id, NOT the default array index.
        // Index keys make the virtualizer reuse measurement slots / rendered
        // rows across reorders & updates, which showed the same card repeated
        // (all cards looked duplicated). A stable key ties each virtual item to
        // its real card.
        getItemKey: (index) => currentCards[index]?._id ?? index,
        estimateSize: (index) => {
            const card = currentCards[index];
            return card?.sizeHint || 120;
        },
        overscan: 5
    });

    // Stable ref callback for measurement.
    //
    // It MUST keep a single identity across renders: an inline
    // `ref={el => measureCardSize(el, index)}` hands React a new function every
    // render, so React detaches+reattaches the ref each render, re-running
    // measureElement → size delta → virtualizer setState → re-render → loop →
    // React error #185 ("Maximum update depth exceeded").
    //
    // It also still records the real measured height into `sizeHint`, because
    // this list's layout math depends on it: `estimateSize` feeds `sizeHint`
    // back, and getTotalSize() + the `translateY(start)` offset (plus the
    // per-card 3px margin accounted for via `* 3`) must reflect real heights or
    // the cards lose their spacing. The element's `data-index` attr tells
    // tanstack which row this is, so no index closure is needed.
    const measureCardSize = useCallback((el) => {
        if (!el) return;
        virtualizer.measureElement(el);
        const index = Number(el.getAttribute('data-index'));
        const card = currentCards[index];
        if (card && Object.isExtensible(card)) {
            card.sizeHint = el.getBoundingClientRect().height;
        }
    }, [virtualizer, currentCards]);


    // Use a simple effect to handle scrolling state
    useEffect(() => {
        const scrollElement = actionListContainerRef.current;
        if (!scrollElement) return;

        // Create a unique ID for this component's timeout
        const timeoutKey = `scrollTimeout_${_id}`;

        const handleScroll = () => {
            setIsScrolling(true);

            // Clear any existing timeout
            if (window[timeoutKey]) {
                clearTimeout(window[timeoutKey]);
            }

            // Set a timeout to clear the scrolling state
            window[timeoutKey] = setTimeout(() => {
                setIsScrolling(false);
            }, 150);
        };

        scrollElement.addEventListener('scroll', handleScroll);

        return () => {
            scrollElement.removeEventListener('scroll', handleScroll);
            if (window[timeoutKey]) {
                clearTimeout(window[timeoutKey]);
                delete window[timeoutKey];
            }
        };
    }, [_id]);


    // Load remaining cards in the background after initial load
    useEffect(() => {
        // Create a unique batch key based on the list ID and current number of cards
        const batchKey = `${_id}-${currentCards.length}`;

        // Only proceed if we have more cards to load and we haven't already loaded this batch
        if (_id && hasMore && !isLoadingMoreCards &&
            currentCards.length < totalCards &&
            !loadedCardBatches.has(batchKey)) {

            // Use a small timeout to ensure the UI remains responsive
            const timer = setTimeout(async () => {
                setIsLoadingMoreCards(true);
                // Mark this batch as being loaded
                setLoadedCardBatches(prev => new Set([...prev, batchKey]));

                const result = await dispatch(fetchCardsByListId({
                    actionListId: _id,
                    skip: currentCards.length,
                    limit: 50,
                    filterQuery
                }))
                    .unwrap();

                const cards = result?.cards || [];

                setBoard((prevBoard) => {
                    // Make sure prevBoard and actionLists exist
                    if (!prevBoard || !prevBoard.actionLists) return prevBoard;

                    const updatedActionLists = prevBoard.actionLists.map((list) => {
                        if (list._id === _id) {
                            // Get existing cards
                            const existingCards = list.cards || [];

                            // Create a Set of existing card IDs for quick lookup
                            const existingCardIds = new Set(existingCards.map(card => card._id));

                            // Filter out cards that already exist in the list
                            const newCards = cards.filter(card => !existingCardIds.has(card._id));

                            return {
                                ...list,
                                cards: [...existingCards, ...newCards]
                            };
                        }
                        return list;
                    });

                    return { ...prevBoard, actionLists: updatedActionLists };
                });

                // Reset loading state after cards are processed
                setIsLoadingMoreCards(false);

            }, 500);

            return () => clearTimeout(timer);
        }
    }, [_id, hasMore, isLoadingMoreCards, currentCards.length, loadedCardBatches, filterQuery, dispatch]);

    const handleBlur = () => {
        if (isListChanged) {
            handleUpdateListTitle(listTitle);
            setListChanged(false);
        }
        setIsEditing(false);
    };

    const virtualCards = virtualizer.getVirtualItems();



    return (
        <div
            style={{
                ...(listHighlight ? { backgroundColor: 'var(--single-card-background-color)', borderRadius: '15px', } : {}),
            }}
            className="action-list-content"
            ref={actionListRef}

        >
            <div
                className={`action-list-inner${isDragging ? ' dragging-start' : ''}`}
                style={style}
            >
                <div className="list-header" ref={listHeaderRef}>

                    <div className="list-title">

                        {!isEditing ? (
                            <span
                                ref={hiddenListRef}
                                className="update-list-title"
                                style={{ cursor: isReadOnly ? 'default' : 'pointer', color: headerTextColor }}
                                onClick={() => {
                                    if (!isReadOnly) {
                                        setIsEditing(true);
                                        setTimeout(() => actionListTitleRef.current.focus(), 0);
                                    }
                                }}
                            >
                                {listTitle || ''}
                            </span>
                        ) : (
                            <textarea
                                ref={actionListTitleRef}
                                className="update-list-title"
                                placeholder="Enter list title"
                                style={{ color: headerTextColor }}
                                rows="1"
                                value={listTitle}
                                required
                                onChange={(e) => {
                                    setListTitle(e.target.value);
                                    setListChanged(true);
                                }}
                                onFocus={(e) => e.target.select()}
                                spellCheck="false"
                                autoCorrect="off"
                                autoCapitalize="off"
                                onBlur={handleBlur}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault(); // Prevents the default behavior of creating a new line
                                        handleBlur();
                                    }
                                }}
                            />
                        )}
                        <div className="action-list-option">

                            <span
                                className="action-list-option-icon"
                                data-tooltip-content="Cards"
                                data-tooltip-position="top"
                            >
                                {
                                    !hasLoadedInitialCards ? (
                                        <BouncingDots size={5} color={headerTextColor} />
                                    ) : (
                                        <span style={{ color: headerTextColor }}>{totalCards}</span>
                                    )
                                }
                            </span>
                            {!isReadOnly && (
                                <span
                                    className="action-list-option-icon"
                                    onClick={() => handleShowForm('top')}
                                    data-tooltip-content="Add a card"
                                    data-tooltip-position="top"
                                >
                                    <Add size={20} color={headerTextColor} />
                                </span>
                            )}

                            {!isReadOnly && (
                                <span
                                    className="action-list-option-icon"
                                    id={'popover-action-list-' + _id}
                                    onClick={(e) => handlePopoverClick(e, 'listAction', { listId: _id, boardId: boardId, workspaceId: board?.wShortId })}
                                    data-tooltip-content="More"
                                    data-tooltip-position="top"
                                    data-popover-trigger
                                >
                                    <MoreHoriz size={20} tooltip={{ content: 'More', position: 'top' }} color={headerTextColor} />
                                </span>
                            )}
                            {visibleListOptions[_id] && (
                                <div className="list-options">
                                    {/* <Link to="#" onClick={handleDeleteList} disabled={isDeleting}>
                                        {isDeleting ? 'Deleting...' : 'Delete'}
                                    </Link> */}
                                    <Link to="#" onClick={handleArchivedList} disabled={isArchiving}>
                                        {isArchiving ? 'Archiving...' : 'Archive'}
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* <span className="card-filters-count">
                        {currentCards?.length} {currentCards?.length === 1 ? 'card matches' : 'cards match'} filters
                    </span> */}

                </div>

                <div
                    className={`action-list-container${isScrolling ? ' scrolling' : ''}`}
                    ref={actionListContainerRef}
                    style={{
                        height: virtualCards.length === 0 ? 0 : virtualizer.getTotalSize() + ((virtualCards.length) * 3),
                        overflowX: 'auto',
                        contain: 'strict',
                        maxHeight: '100%'
                    }}
                >
                    <div
                        className="list-group"
                        style={{
                            height: virtualCards.length === 0 ? 0 : virtualizer.getTotalSize() + ((virtualCards.length) * 3),
                            position: 'relative',
                        }}
                    >
                        <ol
                            className="action-card-list"
                            ref={cardListRef}

                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                transform: `translateY(${virtualCards[0]?.start ?? 0}px)`,
                                width: 'calc(100% - 12px)',
                                overflow: 'hidden',
                                padding: '0 6px',
                            }}
                        >
                            {showTopForm && (
                                <li ref={addNewCardFormRef}>

                                    <div className="add-new-card-form top-card-form">
                                        <form onSubmit={(e) => handleAddCard(e, 'top')}>
                                            <textarea
                                                ref={cardTopTitleRef}
                                                name="add-new-card"
                                                placeholder="Enter card title"
                                                rows="1"
                                                value={cardTitle}
                                                onChange={(e) => setCardTitle(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                        e.preventDefault();
                                                        if (cardTitle.trim() === '') {
                                                            return;
                                                        }
                                                        // Use a callback to handle the event to avoid state update issues
                                                        setTimeout(() => {
                                                            handleAddCard(e, 'top');
                                                        }, 0);
                                                    }
                                                }}
                                                required
                                            />

                                            <div className="footer-buttons">
                                                <button type="submit">Add Card</button>
                                                <button type="button" onClick={handleHideForm}>
                                                    <Close size={18} color={headerTextColor} />

                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </li>

                            )}


                            {virtualCards.map((virtualCard) => {
                                const card = currentCards[virtualCard.index];
                                // Count can briefly lag currentCards during updates.
                                if (!card) return null;
                                return (
                                    <li
                                        key={virtualCard.key}
                                        data-index={virtualCard.index}
                                        ref={measureCardSize}
                                        data-cardid={card._id || `card-${_id}-${virtualCard.index}`}

                                    >
                                        <Card
                                            card={card}
                                            actionListId={_id}
                                            moveCard={moveCard}
                                            moveActionList={moveActionList}
                                            setBoard={setBoard}
                                            isCardDragging={isCardDragging}
                                            setCardDragging={setCardDragging}
                                            isReadOnly={isReadOnly}
                                        />
                                    </li>
                                );
                            })}

                        </ol>

                    </div>

                </div>

                {
                    !showForm && !isReadOnly && (
                        <div className="add-new-card-form-button" ref={addNewCardFormRef}>
                            <AddCardButton onClick={handleShowForm} color={headerTextColor} bgColor={listBgColor} />
                        </div>
                    )
                }

                {showForm && !isReadOnly && (

                    <div className="add-new-card-form" ref={addNewCardFormRef}>

                        <form onSubmit={(e) => handleAddCard(e, 'bottom')}>
                            <textarea
                                ref={cardTitleRef}
                                name="add-new-card"
                                placeholder="Enter card title"
                                rows="1"
                                value={cardTitle}
                                onChange={(e) => setCardTitle(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        if (cardTitle.trim() === '') {
                                            return;
                                        }
                                        // Use a callback to handle the event to avoid state update issues
                                        setTimeout(() => {
                                            handleAddCard(e, 'bottom');
                                        }, 0);
                                    }
                                }}
                                required
                            />

                            <div className="footer-buttons">
                                <button type="submit">Add Card</button>
                                <button type="button" onClick={handleHideForm}>
                                    <Close size={18} color={headerTextColor} />
                                </button>
                            </div>
                        </form>
                    </div>

                )}

            </div>


        </div>
    );
};

export default ActionListPND;
